import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import useProgressHeartbeat from './useProgressHeartbeat';

const Wrap = styled.div`
  background: #fff;
  border: 1px solid #E0E4E8;
  border-radius: 6px;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #F8FAFC;
  border-bottom: 1px solid #E0E4E8;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const NavBtn = styled.button`
  background: transparent;
  border: 1px solid #CBD5E1;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.text.primary};
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const IframeHost = styled.iframe`
  width: 100%;
  height: 70vh;
  border: none;
  display: block;
`;

/**
 * Lightweight PDF viewer.
 *
 * Uses the browser's native PDF plugin via iframe + "#page=N" anchor so we
 * don't pull in a heavy JS PDF library for MVP. The learner uses the "next
 * page" button below the viewer to advance — that click is what credits the
 * page as viewed. Completion = every page opened at least once.
 */
export default function PdfViewer({
  contentItem,
  assignmentId,
  onCompleted,
  onProgress,
}) {
  const { t } = useTranslation();
  const pageCount = contentItem.pageCount || 1;
  const url = contentItem.url;

  const [page, setPage] = useState(() => {
    const seen = contentItem.viewedPages || [];
    return seen.length > 0 ? Math.min(Math.max(...seen), pageCount) : 1;
  });

  const viewedRef = useRef(new Set(contentItem.viewedPages || [1]));
  // Track prior completion so we only call onCompleted on a fresh transition.
  // Without this, every heartbeat that returns isCompleted re-fires the parent
  // refetch, which unmounts the iframe and the user sees a constant refresh.
  const completedFiredRef = useRef(contentItem.isCompleted === true);

  const getState = useCallback(() => ({
    contentItemId: contentItem.id,
    viewedPages: Array.from(viewedRef.current),
    clientCompleted: viewedRef.current.size >= pageCount,
  }), [contentItem.id, pageCount]);

  const flushRef = useRef(null);
  flushRef.current = useProgressHeartbeat(assignmentId, getState, (res) => {
    if (res?.isCompleted && !completedFiredRef.current) {
      completedFiredRef.current = true;
      onCompleted?.();
    }
    onProgress?.(res);
  });

  useEffect(() => {
    viewedRef.current.add(page);
    // Fire a heartbeat on every page change so partial completion sticks even
    // if the learner closes the tab right after.
    flushRef.current?.();
  }, [page]);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(pageCount, p + 1));

  const src = `${url}#page=${page}&toolbar=1&navpanes=0`;

  return (
    <Wrap>
      <Toolbar>
        <div>{contentItem.originalFileName || 'PDF'}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NavBtn type="button" onClick={goPrev} disabled={page <= 1}>
            <ChevronLeft size={14} /> {t('training.prevPage')}
          </NavBtn>
          <span style={{ minWidth: 60, textAlign: 'center' }}>{page} / {pageCount}</span>
          <NavBtn type="button" onClick={goNext} disabled={page >= pageCount}>
            {t('training.nextPage')} <ChevronRight size={14} />
          </NavBtn>
        </div>
      </Toolbar>
      <IframeHost title={contentItem.originalFileName || 'PDF'} src={src} />
    </Wrap>
  );
}
